import { _, helpers, React, ts, ui, useSelector } from '_core';

import { prepareAnnuStatsData } from './helpers';
import { Params } from './types';
import { nestedBarChartFormat } from '../../common/formatters/nested-bar-chart';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  portfolios: string;
  fullScreen: boolean;
  params: Params;
  context: ts.types.basket.BasketAnalysisContext;
  title: string;
};

const Chart = ({ id, readOnly, fullScreen, loadWidgetData, portfolios, params, context, title }: ChartProps) => {
  const baskets = useSelector((state) => state.resources.baskets);

  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: portfolios,
        },
      });
      if (response) {
        const newData = _(response.data)
          .groupBy((item) => `${item.history}_${item.type}`)
          .map((group, key) => {
            const [history, type] = key.split('_');
            const result = group.reduce((acc, item) => {
              acc[item.handle as string] =
                params.statistics_type === 'risk'
                  ? params.with_benchmark
                    ? item.active_risk
                    : item.portfolio_risk
                  : params.with_benchmark
                    ? item.active_returns
                    : item.portfolio_returns;
              return acc;
            }, {});

            return {
              ...result,
              type,
              history,
            };
          })
          .value();

        setData(newData);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };
  React.useEffect(() => {
    if (portfolios) loadData();
    return () => setData(null);
  }, [portfolios]);

  const formattedData = React.useMemo(() => {
    if (_.isNil(data)) return null;
    return prepareAnnuStatsData(nestedBarChartFormat(data), params, context, baskets);
  }, [data, params, context, baskets]);

  return (
    <ui.NestedBarChart
      id={id}
      data={formattedData?.data}
      count={formattedData?.count}
      numberFormat={params.statistics_type !== 'adjusted' ? '#.##%' : '#.##'}
      category="category"
      alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
      fullScreen={fullScreen}
      exportTitle={title}
      readOnly={readOnly}
    />
  );
};

export default Chart;
